/**
 * WHAT A NON-ADMIN'S CHROME LOOKS LIKE — the header, the messages icon, and six toolbar actions.
 *
 * <h2>One module, because the rule spans three files</h2>
 *
 * The header lives in `App.js`, the messages icon and the toolbar in `TabPanel.js`, and two of the
 * six actions are GENERATED in `cycleToolbarButtons.js`. Written at each site, "what a customer
 * sees" would be four scattered conditionals and no way to read the answer off one screen — and
 * the half somebody forgot would be invisible, because a control that should be hidden and is not
 * looks exactly like a control that was always meant to be there.
 *
 * <h2>Gated, never deleted</h2>
 *
 * Every handler behind these six actions is untouched and still wired; the engines, the cycle
 * presets and Alpha Replay all still run for Admin and all still have their tests. This list
 * is the only thing standing between them and a customer's toolbar.
 *
 * <h2>This is the ROLE gate, which is not the PROFILE gate</h2>
 *
 * `toolbarProfiles.js` answers "which of the three audiences is this toolbar for", chosen from a
 * dev dropdown. This answers "is the signed-in user an admin", which comes from their own profile.
 * They are different questions and a control must clear both — see `isActionVisibleForProfile`.
 */

import { ASTRO, INTEGRALDELTA, DHANABHYAS, ADMIN } from './productTarget';

/**
 * The title a customer sees on the ASTRO product, exactly.
 *
 * <p>⚠ KEPT AS THE ASTRO VALUE, NOT THE GENERIC ONE. `utils/hostContext.js` uses it as the ASC
 * half of a two-way choice and two tests pin the literal, so it stays what it is. What changed is
 * that the HEADER no longer reads it directly — see {@link nonAdminBrandFor}.
 */
export const NON_ADMIN_BRAND = 'AstroStockCharts.Com';

/**
 * THE TITLE A CUSTOMER SEES, BY BUILD TARGET.
 *
 * <h2>⚠ THIS WAS THE "integralDelta STILL LOOKS LIKE AstroStockCharts" BUG</h2>
 *
 * `App.js` read {@link NON_ADMIN_BRAND} directly, so EVERY non-admin on EVERY build saw
 * `AstroStockCharts.Com` in the header — including a paying customer on an integralDelta build.
 * There was no product in the expression at all, which is why no amount of setting a user's
 * `role` to `integraldelta` could change it: the header was a constant.
 *
 * <p>⚠ KEYED ON THE BUILD TARGET, NOT ON THE USER'S ROLE, and that is deliberate. The product a
 * deployment IS comes from `productTarget.json`; a role is an ENTITLEMENT carried by a person.
 * Branding the shell from the signed-in user would mean two customers on one deployment seeing
 * two different product names, and an anonymous visitor — who has no role at all — seeing
 * neither. The same argument the product target was introduced with.
 *
 * <p>⚠ AND NOT ON THE HOSTNAME EITHER. `getHostBrandName` answers this question from
 * `resolveHostContext`, which DEFAULTS TO ASC for any domain it does not recognise — including
 * `localhost`. That default is exactly why an integralDelta build reads as AstroStockCharts in
 * local development, and it is the trap `describeHostContext` was written to expose.
 *
 * @param product one of PRODUCT_TARGETS. ⚠ ANYTHING UNKNOWN FALLS BACK TO THE ASTRO TITLE, so a
 *   target added without a case here ships the US brand to that product's customers in silence —
 *   `productVisibility.test.js` asserts every target resolves to a brand it declares, rather than
 *   leaving the default to be discovered.
 */
export const nonAdminBrandFor = (product) => {
  switch (product) {
    case INTEGRALDELTA: return 'integralDelta.ai';
    /*
     * ⚠ EXPLICIT, BECAUSE THE DEFAULT BELOW IS 'AstroStockCharts.Com'.
     *
     * A fourth target taking the default silently would put the US brand in the header of every
     * India customer's chart — the exact defect this function was written to fix for
     * integralDelta, reintroduced by adding a value and not a case. The same shape this
     * repository records for `Market` gaining COMMODITIES and for `EngineMode` gaining a third
     * value, where three of four two-way checks would have taken the wrong branch.
     */
    case DHANABHYAS: return 'Dhanabhyas.com';
    /*
     * ⚠ THE ASTRO BUILD IS BRANDED `Dhanabhyas` — an EXPLICIT case, not a change to
     * {@link NON_ADMIN_BRAND}.
     *
     * That constant is still `AstroStockCharts.Com` because `utils/hostContext.js` uses it as the
     * ASC half of a two-way host choice and two tests pin the literal. Editing it to rebrand the
     * header would have silently moved host resolution as well — a second, invisible effect of a
     * cosmetic change, which is precisely the shape of bug this function was written to fix.
     *
     * <p>⚠ IT DIFFERS FROM {@code DHANABHYAS}'s own brand by the suffix, and that is left as it
     * stands rather than quietly unified. `dhanabhyas` is a separate build target serving a
     * separate site, and deciding that two targets share one name is a product decision, not a
     * tidy-up to be performed on the way past.
     */
    case ASTRO: return 'Dhanabhyas';
    // The internal build shows the astro title: `admin` is not a product a customer is ever
    // served, so there is no third brand to invent, and borrowing integralDelta's name would
    // misreport which product an internal screenshot came from.
    case ADMIN:
    default: return NON_ADMIN_BRAND;
  }
};

/**
 * THE SIX ACTIONS A CUSTOMER DOES NOT GET, which makes the bar six icons shorter for them.
 *
 * <p>Five of them are one run — the two cycle presets, then the three engine runners — and
 * everything between those carried `adminOnly` at the point of declaration, so a customer never
 * saw a gap. The sixth is **Alpha Replay**, which closed that run until 2026-10-20 and now sits
 * beside the two simulator icons much further along the bar.
 *
 * <p>⚠ SO THIS IS A SET, NOT A SPAN, and it always was in the only sense that matters — it is
 * named by ACTION rather than by position, because three of the entries in that stretch are
 * spreads of generated arrays and an index into the icon list is not stable against adding one
 * orb. Moving an icon therefore costs nothing here, which is the point of naming them.
 *
 * <p>Declared here rather than as `adminOnly: true` on each entry because two of the six are
 * generated in another module and three carry a comment explaining why they are NOT gated. Six
 * flags across three files is six places for the next reader to find five of.
 *
 * <p>`toggle_simulate` keeps its name though the control is now called Alpha Replay. An action id
 * is the wire between a toolbar entry and the dispatcher's `switch`, visible to nobody — renaming
 * it would touch this list, the entry, the case and four test files, and the whole risk of that
 * change is in the one place it was missed. The comment is what carries the product name.
 */
export const ADMIN_ONLY_TOOLBAR_ACTIONS = [
  'run_cycle_long',    // PTA — Long Cycle
  'run_cycle_short',   // PTB — Short Cycle
  'run_engine_pta',    // Run PTA
  'run_engine_ptb',    // Run PTB
  'run_engine_cts',    // Run CTS
  'toggle_simulate',   // Alpha Replay
];

/**
 * THE MESSAGES ICONS — there are TWO, and both are hidden. ⚠ READ THE COST.
 *
 * <pre>
 *   mid-toolbar, alt "Messages"          open_messages_dialog   the MESSAGE CENTRE
 *   far right,   "Show Messages Panel"   (no action, JSX gate)  the STUDY OUTPUT panel
 * </pre>
 *
 * <p>"Remove the messages icon" names one thing and the toolbar has two, so both go — leaving one
 * standing with "Messages" as its literal label would plainly not be what was asked for.
 *
 * <p><b>The Message Centre is a CUSTOMER-FACING feature and hiding it has a real cost.</b>
 * `MessagesDialog` is explicitly read-only for a customer and editable for an admin; it is how
 * the team publishes a notice and how a customer reads one. With the icon gone there is no other
 * route to it, so announcements stop reaching customers. Nothing server-side changed — the
 * endpoint, the permission check and the dialog are untouched — and restoring it is deleting ONE
 * STRING from this array.
 *
 * <p>The study-output panel is the easier half: a customer no longer has the six engine and cycle
 * buttons that populate it, so the envelope would open an empty panel most of the time.
 */
export const ADMIN_ONLY_MESSAGE_ACTIONS = [
  'open_messages_dialog',
];

/**
 * THE ONE ACTION THAT WENT THE OTHER WAY, and it is recorded rather than merely un-flagged.
 *
 * <p>The Φ reversal scan was `adminOnly: true` while the study was being evaluated. That flag is
 * now gone from its declaration — the declaration is where a reader looks, so a second mechanism
 * here overriding it would be two answers to one question.
 *
 * <p>What this constant is for is the OTHER half: a test asserts the entry carries no `adminOnly`,
 * so the six-out and the one-in halves of this change are legible together and neither can be
 * quietly undone on its own.
 */
export const CUSTOMER_FACING_SCAN_ACTION = 'spiral_reversal_scan';

/** Every action a non-admin does not get, from both lists above. */
export const ADMIN_ONLY_ACTIONS = [
  ...ADMIN_ONLY_TOOLBAR_ACTIONS,
  ...ADMIN_ONLY_MESSAGE_ACTIONS,
];

/** Whether {@code action} is hidden from a non-admin. */
export const isAdminOnlyAction = (action) =>
  !!action && ADMIN_ONLY_ACTIONS.includes(action);

/**
 * The single gate every toolbar entry passes through.
 *
 * <p>`adminOnly` at the declaration is still the primary mechanism and is checked first; this adds
 * the block above, which could not be expressed that way.
 *
 * @param {{action?: string, adminOnly?: boolean}} icon the entry as declared
 * @param {boolean} isAdmin whether the signed-in user is an admin
 */
/**
 * ⚠ THE ADMIN-ONLY GATE IS LIFTED FOR THESE, ON THESE PRODUCTS.
 *
 * <h2>Why an exemption rather than deleting the entries</h2>
 *
 * The obvious change was to remove `run_cycle_long`, `run_cycle_short` and `toggle_simulate` from
 * {@link ADMIN_ONLY_TOOLBAR_ACTIONS}. That would also reveal them on ASTRO, where they are
 * withheld on purpose — the cycle presets and Alpha Replay are research surfaces there, and the
 * astro deny list separately removes the two pills from that product entirely. One list cannot
 * mean "withheld from customers" and "withheld from astro customers only" at the same time, so
 * the product-specific answer gets its own list and the global one keeps its meaning.
 *
 * <h2>⚠ AND THE CTS ORBS ARE HERE FOR A DIFFERENT REASON</h2>
 *
 * `ctsIconEntries()` sets `adminOnly: true` on the ENTRY, not through the action list, so the
 * exemption has to be tested BEFORE `icon.adminOnly` — which is why the check sits where it does
 * below. Reading the entry flag first would make these three unreachable and the symptom would be
 * three triangles that never appear, with every list in this file looking correct.
 *
 * <p>These are a deliberate widening of what a paying integralDelta customer sees: the cycle
 * pills, the three CTS orbs, Alpha Replay and the Message Centre are that product's own surface.
 */
export const CUSTOMER_FACING_BY_PRODUCT = Object.freeze({
  [INTEGRALDELTA]: Object.freeze([
    'run_cycle_long',       // the PTA pill
    'run_cycle_short',      // the PTB pill
    'cts_scan_1',           // the three CTS orbs, badged 1/2/3
    'cts_scan_2',
    'cts_scan_3',
    'toggle_simulate',      // Alpha Replay
    'open_messages_dialog', // the Message Centre — read-only for a customer
    // The MARKET SCANNER. ⚠ This entry and the backend's `allowsIntegralDeltaFeature()` are
    // one rule in two places and must agree: the server refuses an astro customer with
    // 403, so the icon must stay hidden there — which is what its `adminOnly: true`
    // does — and must appear here, which is what this does.
    'run_market_scanner',
    /*
     * ⚠ `open_dynaman_simulator` AND `launch_simulator` WERE HERE AND ARE DELIBERATELY GONE.
     *
     * They are not withheld from anybody any more — DynaMan and the Verifiable Alpha Workbench are
     * open to every role and plan on every product — so the `adminOnly: true` on their entries was
     * removed instead. Keeping them listed here as well would leave a flag and an exemption that
     * cancel out: two mechanisms for one answer, where editing either looks like it should change
     * something and does not.
     *
     * They are a DELETION rather than a move to another list, and the backend matches: the
     * simulation guard requires a signed-in user rather than an administrator. If either ever
     * needs narrowing again, the entry flag is the place — this list is for "the global gate is
     * lifted on THIS product", which is a different statement from "there is no gate".
     */
  ]),
});

/** Whether the admin-only gate is lifted for {@code action} on {@code product}. */
export const isCustomerFacingOnProduct = (action, product) =>
  !!action && (CUSTOMER_FACING_BY_PRODUCT[product] || []).includes(action);

/**
 * Whether {@code icon} is rendered for this role, on this product.
 *
 * <p>⚠ `product` IS OPTIONAL AND ITS ABSENCE IS THE OLD BEHAVIOUR. Omitted, no exemption applies
 * and every caller and test written before this gate existed keeps its exact answer — which is
 * the narrower one, so a caller that forgets the argument hides a control rather than revealing
 * one.
 */
export const isIconVisibleForRole = (icon, isAdmin, product) => {
  if (isAdmin) return true;
  const action = icon && icon.action;
  // BEFORE the entry flag, or the CTS orbs — which carry `adminOnly` on the entry — could never
  // be exempted. See the note on CUSTOMER_FACING_BY_PRODUCT.
  if (isCustomerFacingOnProduct(action, product)) return true;
  if (icon && icon.adminOnly) return false;
  return !isAdminOnlyAction(action);
};

export default isIconVisibleForRole;
